// VetCheckupTypesPage.js
// CRUD page for vet_checkup_types table.
// - API base: process.env.REACT_APP_API_URL
// - Endpoints (adjust if needed):
//   GET    /checkup/types
//   POST   /checkup/types
//   PUT    /checkup/types/:id
//   DELETE /checkup/types/:id
//
// Notes:
// - Comments are in English (as requested).
// - Uses JWT from localStorage.getItem('token').
// - i18n used only for dir switching (optional). You can remove if not needed.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  Chip,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';

const apiUrl = process.env.REACT_APP_API_URL; // e.g., http://192.168.1.251:3360

const SEVERITIES = ['low', 'medium', 'high', 'critical'];

const VetCheckupTypesPage = () => {
  const { i18n } = useTranslation();
  const dir = i18n?.language === 'ar' ? 'rtl' : 'ltr';
  const align = i18n?.language === 'ar' ? 'right' : 'left';

  // Data state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // Search & sort
  const [q, setQ] = useState('');
  const [sortField, setSortField] = useState('category_ar');
  const [sortDir, setSortDir] = useState('asc');

  // Create/Edit dialog
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    category_ar: '',
    category_en: '',
    name_ar: '',
    name_en: '',
    severity_default: 'low',
    active: true,
  });

  // Delete dialog
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  // ------- Helpers
  const authHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await axios.get(`${apiUrl}/checkup/types/active`, {
        headers: authHeader(),
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setRows(data);
    } catch (e) {
      setErr(e.response?.data?.message || 'فشل في جلب البيانات');
    } finally {
      setLoading(false);
    }
  }, []);

  const createRow = async (payload) => {
    await axios.post(`${apiUrl}/checkup/types`, payload, {
      headers: authHeader(),
    });
  };
  const updateRow = async (id, payload) => {
    await axios.put(`${apiUrl}/checkup/types/${id}`, payload, {
      headers: authHeader(),
    });
  };
  const deleteRow = async (id) => {
    await axios.delete(`${apiUrl}/checkup/types/${id}`, {
      headers: authHeader(),
    });
  };

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // ------- Sort/Search
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };
  const sortArrow = (field) =>
    sortField === field ? (sortDir === 'asc' ? '▲' : '▼') : '';

  const filtered = useMemo(() => {
    const norm = (v) => String(v ?? '').toLowerCase();
    const searched = rows.filter((r) => {
      return (
        norm(r.category_ar).includes(q.toLowerCase()) ||
        norm(r.category_en).includes(q.toLowerCase()) ||
        norm(r.name_ar).includes(q.toLowerCase()) ||
        norm(r.name_en).includes(q.toLowerCase()) ||
        norm(r.severity_default).includes(q.toLowerCase())
      );
    });

    const sortVal = (r) => {
      if (sortField === 'active') return Number(r.active ?? 0);
      return String(r[sortField] ?? '');
    };

    return searched.sort((a, b) => {
      const va = sortVal(a);
      const vb = sortVal(b);
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
  }, [rows, q, sortField, sortDir]);

  // ------- UI handlers
  const openCreate = () => {
    setEditing(null);
    setForm({
      category_ar: '',
      category_en: '',
      name_ar: '',
      name_en: '',
      severity_default: 'low',
      active: true,
    });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      category_ar: row.category_ar || '',
      category_en: row.category_en || '',
      name_ar: row.name_ar || '',
      name_en: row.name_en || '',
      severity_default: row.severity_default || 'low',
      active: (row.active ?? 1) === 1 || row.active === true,
    });
    setOpen(true);
  };

  const validateForm = () => {
    // Minimal validation
    if (!form.name_ar.trim() && !form.name_en.trim()) {
      return 'يرجى إدخال اسم الفحص بالعربي أو الإنجليزي';
    }
    if (!SEVERITIES.includes(form.severity_default)) {
      return 'قيمة مستوى الخطورة غير صحيحة';
    }
    return '';
  };

  const handleSave = async () => {
    setErr('');
    const v = validateForm();
    if (v) {
      setErr(v);
      return;
    }

    const payload = {
      // match DB columns exactly
      category_ar: form.category_ar.trim(),
      category_en: form.category_en.trim(),
      name_ar: form.name_ar.trim(),
      name_en: form.name_en.trim(),
      severity_default: form.severity_default,
      active: form.active ? 1 : 0,
    };

    try {
      if (editing) {
        const id = editing.type_id ?? editing.id;
        await updateRow(id, payload);
      } else {
        await createRow(payload);
      }
      setOpen(false);
      await fetchRows();
    } catch (e) {
      // Unique index on key_name might trigger here
      const msg = e.response?.data?.message;
      if (
        msg?.toLowerCase?.().includes('key_name') ||
        msg?.toLowerCase?.().includes('duplicate')
      ) {
        setErr('قيمة (Key Name) مستخدمة مسبقًا، الرجاء اختيار قيمة فريدة.');
      } else {
        setErr(msg || 'فشل الحفظ');
      }
    }
  };

  const askDelete = (row) => {
    setDeleteId(row.type_id ?? row.id);
    setConfirmText('');
    setOpenDelete(true);
  };

  const confirmDelete = async () => {
    if (confirmText.trim().toUpperCase() !== 'DELETE') {
      setErr('يرجى كتابة DELETE للتأكيد');
      return;
    }
    try {
      await deleteRow(deleteId);
      setOpenDelete(false);
      await fetchRows();
    } catch (e) {
      setErr(e.response?.data?.message || 'فشل حذف السجل');
    }
  };

  const exportToExcel = () => {
    const data = rows.map((r) => ({
      ID: r.type_id,
      'التصنيف (عربي)': r.category_ar,
      'Category (EN)': r.category_en,
      'اسم الفحص (عربي)': r.name_ar,
      'Checkup Name (EN)': r.name_en,
      Severity: r.severity_default,
      Active: r.active ?? 0 ? 'Yes' : 'No',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Vet Checkup Types');
    const excelBuffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    saveAs(blob, `vet_checkup_types_${ts}.xlsx`);
  };

  // ------- Render
  return (
    <Container maxWidth="lg" dir={dir}>
      {/* Header (أعلى الصفحات) */}
      <Box
        sx={{
          mt: 2.5,
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: dir === 'rtl' ? 'row-reverse' : 'row',
          gap: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          أنواع فحوصات العيادة البيطرية
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder="بحث بالتصنيف/الاسم/المفتاح..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
          />
          <Tooltip title="تصدير إلى Excel">
            <IconButton color="success" onClick={exportToExcel}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            إضافة نوع فحص
          </Button>
        </Box>
      </Box>

      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      <Paper sx={{ p: 0 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">#</TableCell>
              <TableCell
                align={align}
                sx={{ cursor: 'pointer' }}
                onClick={() => toggleSort('category_ar')}
              >
                التصنيف (عربي) {sortArrow('category_ar')}
              </TableCell>
              <TableCell
                align={align}
                sx={{ cursor: 'pointer' }}
                onClick={() => toggleSort('category_en')}
              >
                Category (EN) {sortArrow('category_en')}
              </TableCell>

              <TableCell
                align={align}
                sx={{ cursor: 'pointer' }}
                onClick={() => toggleSort('name_ar')}
              >
                اسم الفحص (عربي) {sortArrow('name_ar')}
              </TableCell>
              <TableCell
                align={align}
                sx={{ cursor: 'pointer' }}
                onClick={() => toggleSort('name_en')}
              >
                Checkup Name (EN) {sortArrow('name_en')}
              </TableCell>
              <TableCell
                align="center"
                sx={{ cursor: 'pointer' }}
                onClick={() => toggleSort('severity_default')}
              >
                مستوى الخطورة {sortArrow('severity_default')}
              </TableCell>
              <TableCell
                align="center"
                sx={{ cursor: 'pointer' }}
                onClick={() => toggleSort('active')}
              >
                نشط {sortArrow('active')}
              </TableCell>
              <TableCell align="center">إجراءات</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading &&
              filtered.map((r, idx) => (
                <TableRow key={r.type_id ?? idx}>
                  <TableCell align="center">{idx + 1}</TableCell>
                  <TableCell align={align}>{r.category_ar || '-'}</TableCell>
                  <TableCell align={align}>{r.category_en || '-'}</TableCell>

                  <TableCell align={align}>{r.name_ar || '-'}</TableCell>
                  <TableCell align={align}>{r.name_en || '-'}</TableCell>
                  <TableCell align="center">
                    <Chip label={r.severity_default || '-'} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    {r.active ?? 0 ? 'نعم' : 'لا'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton color="secondary" onClick={() => openEdit(r)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => askDelete(r)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            {loading && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  جارِ التحميل…
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  لا توجد بيانات
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
        dir={dir}
      >
        <DialogTitle>
          {editing ? 'تعديل نوع الفحص' : 'إضافة نوع فحص'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="التصنيف بالعربي"
              value={form.category_ar}
              onChange={(e) =>
                setForm({ ...form, category_ar: e.target.value })
              }
            />
            <TextField
              label="Category (EN)"
              value={form.category_en}
              onChange={(e) =>
                setForm({ ...form, category_en: e.target.value })
              }
            />

            <TextField
              label="اسم الفحص بالعربي"
              value={form.name_ar}
              onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
            />
            <TextField
              label="Checkup Name (EN)"
              value={form.name_en}
              onChange={(e) => setForm({ ...form, name_en: e.target.value })}
            />
            <TextField
              select
              label="مستوى الخطورة"
              value={form.severity_default}
              onChange={(e) =>
                setForm({ ...form, severity_default: e.target.value })
              }
            >
              {SEVERITIES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(e) =>
                    setForm({ ...form, active: e.target.checked })
                  }
                />
              }
              label="نشط"
              sx={{ justifySelf: dir === 'rtl' ? 'end' : 'start' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>إلغاء</Button>
          <Button variant="contained" onClick={handleSave}>
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)} dir={dir}>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>اكتب كلمة DELETE للتأكيد</Typography>
          <TextField
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>إلغاء</Button>
          <Button color="error" onClick={confirmDelete}>
            حذف
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VetCheckupTypesPage;
